import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, X, ChevronDown, Check } from 'lucide-react';
import { ralColors, colorCategories, getColorById } from '../data/ralColors';
import { colors as defaultColors } from '../data/mockData';

const ColorPicker = ({ selectedColor, onColorChange, showSearch = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Combine default colors with RAL colors, avoiding duplicates
  const allColors = useMemo(() => {
    const defaultColorIds = new Set(defaultColors.map(c => c.id));
    const ralColorsMapped = ralColors.map(ral => ({
      id: `ral-${ral.ral}`,
      name: `RAL ${ral.ral}`,
      hex: ral.hex,
      ral: ral.ral,
      category: ral.category
    }));
    
    return [...defaultColors, ...ralColorsMapped];
  }, []);

  // Filter colors based on search and category
  const filteredColors = useMemo(() => {
    let filtered = allColors;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(color => color.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(color => 
        color.name.toLowerCase().includes(term) ||
        color.ral?.toLowerCase().includes(term) ||
        color.id.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [allColors, searchTerm, selectedCategory]);

  // Get selected color details
  const selectedColorData = useMemo(() => {
    return allColors.find(c => c.id === selectedColor) || allColors[0];
  }, [allColors, selectedColor]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setSelectedCategory('all');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleColorSelect = (color) => {
    onColorChange(color.id);
    setIsOpen(false);
    setSearchTerm('');
    setSelectedCategory('all');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Select Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 h-9 px-3 py-2 text-sm border border-input bg-transparent rounded-md shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div
            className="w-5 h-5 rounded border border-gray-300 flex-shrink-0"
            style={{ backgroundColor: selectedColorData.hex }}
          />
          <span className="text-left truncate">
            {selectedColorData.ral ? `RAL ${selectedColorData.ral}` : selectedColorData.name}
          </span>
        </div>
        <ChevronDown className={`h-4 w-4 opacity-50 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Content */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg flex flex-col" style={{ maxHeight: '350px' }}>
          {/* Search Input - Fixed at top */}
          {showSearch && (
            <div className="p-2 border-b border-gray-200 flex-shrink-0 bg-white">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search by RAL code or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {/* Category Filter - Fixed at top */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {colorCategories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-2 py-1 text-xs rounded-full border transition-all ${
                      selectedCategory === category
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-orange-300'
                    }`}
                  >
                    {category === 'all' ? 'All' : category.charAt(0).toUpperCase() + category.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Color List - Scrollable with fixed height */}
          <div className="overflow-y-auto overflow-x-hidden" style={{ maxHeight: showSearch ? '280px' : '320px', minHeight: '100px' }}>
            {filteredColors.length > 0 ? (
              <div className="p-1">
                {filteredColors.map(color => (
                  <button
                    key={color.id}
                    onClick={() => handleColorSelect(color)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors ${
                      selectedColor === color.id ? 'bg-orange-50' : ''
                    }`}
                  >
                    <div
                      className="w-8 h-8 rounded border border-gray-300 flex-shrink-0"
                      style={{ backgroundColor: color.hex }}
                    />
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {color.ral ? `RAL ${color.ral}` : color.name}
                      </div>
                      {color.ral && color.name !== `RAL ${color.ral}` && (
                        <div className="text-xs text-gray-500 truncate">{color.name}</div>
                      )}
                    </div>
                    {selectedColor === color.id && (
                      <Check className="w-4 h-4 text-orange-500 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-sm text-gray-500">
                No colors found
              </div>
            )}
          </div>

          {/* Results count - Fixed at bottom */}
          {(searchTerm || selectedCategory !== 'all') && filteredColors.length > 0 && (
            <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-200 flex-shrink-0 bg-gray-50">
              {filteredColors.length} color{filteredColors.length !== 1 ? 's' : ''} found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ColorPicker;

